// System
import { useEffect } from 'react';
import { useSnackbar } from 'notistack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Divider, Stack, MenuItem, IconButton, Box, Link } from '@mui/material';

// Components
import { RHFSelect, RHFTextField } from '@Components/hook-form';
import { AtLocator } from '@Components/shared/locators/AtLocator';

// Hooks
import { useAuthorization } from '@Modal/hooks/authorization/useAuthorization';
import { useProviderDrawer } from '@Modal/hooks/providers/useProviderDrawer';

// Graphql
import { useGetProviderPublicKeyLazyQuery } from '@Src/generated.graphql';

export const SetupProviderDetails: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { userPermissions } = useAuthorization();
  const { selectedProviderData } = useProviderDrawer();
  const [getProviderPublicKey, { data }] = useGetProviderPublicKeyLazyQuery();
  const permissionToViewPublicKey = userPermissions?.includes('CAN_VIEW_PROVIDER_PUBLIC_KEY');

  // const demoUrl = 'https://client.crush.sigma-bet.com:81';
  // const documentation = 'https://docs.crush.theavigroup.com';
  // const currentUrl = window?.location?.origin;
  // const clientUrl = currentUrl?.replace('office.', '')?.replace('/providers', '');

  useEffect(() => {
    if (permissionToViewPublicKey && selectedProviderData?.id) {
      getProviderPublicKey({
        variables: {
          providerId: selectedProviderData.id,
        },
      });
    }
  }, []);

  const handleCopy = () => {
    if (data?.providerPublicKey) {
      navigator.clipboard.writeText(data?.providerPublicKey).then(() => {
        enqueueSnackbar('Public key copied', {
          autoHideDuration: 2000,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
        });
      });
    }
  };

  return (
    <Stack direction="column" spacing={2} margin={2}>
      <AtLocator type="text-field" name="name" key="name">
        <RHFTextField name="name" label="Name" />
      </AtLocator>
      <AtLocator type="text-field" name="apiUrl" key="apiUrl">
        <RHFTextField name="apiUrl" label="Api Url" />
      </AtLocator>
      <RHFSelect
        label="Status"
        name="status"
        variant="outlined"
        sx={{ width: '100%', marginTop: '6px', marginBottom: '15px' }}
      >
        <MenuItem value="ACTIVE">Active</MenuItem>
        <MenuItem value="INACTIVE">Inactive</MenuItem>
      </RHFSelect>
      <RHFSelect label="Is Internal" name="isInternal" variant="outlined">
        <MenuItem value="true">True</MenuItem>
        <MenuItem value="false">False</MenuItem>
      </RHFSelect>
      <RHFSelect
        label="Sync Round End?"
        name="syncRoundEnd"
        variant="outlined"
        sx={{ width: '100%', marginTop: '6px', marginBottom: '15px' }}
      >
        <MenuItem value="true">True</MenuItem>
        <MenuItem value="false">False</MenuItem>
      </RHFSelect>
      <RHFSelect
        label="Retry Scheduled Send Credit"
        name="retryScheduledSendCredit"
        variant="outlined"
      >
        <MenuItem value="true">True</MenuItem>
        <MenuItem value="false">False</MenuItem>
      </RHFSelect>
      <RHFSelect label="Refund On Credit Fail" name="refundOnCreditFail" variant="outlined">
        <MenuItem value="true">True</MenuItem>
        <MenuItem value="false">False</MenuItem>
      </RHFSelect>
      <RHFSelect
        label="Filter External Provider Bets"
        name="filterExternalProviderBets"
        variant="outlined"
      >
        <MenuItem value="true">True</MenuItem>
        <MenuItem value="false">False</MenuItem>
      </RHFSelect>
      <RHFSelect label="Integration Type" name="integrationType" variant="outlined">
        <MenuItem value="DIRECT">DIRECT</MenuItem>
        <MenuItem value="REVERSE_BGI">REVERSE_BGI</MenuItem>
      </RHFSelect>
      <RHFSelect label="Raindrop Creation Notification" name="syncRaindrop" variant="outlined">
        <MenuItem value="true">True</MenuItem>
        <MenuItem value="false">False</MenuItem>
      </RHFSelect>
      <RHFSelect label="Freebet Creation Notification" name="syncFreebet" variant="outlined">
        <MenuItem value="true">True</MenuItem>
        <MenuItem value="false">False</MenuItem>
      </RHFSelect>
      <RHFSelect label="Notify On Last Freebet" name="notifyOnLastFreebet" variant="outlined">
        <MenuItem value="true">True</MenuItem>
        <MenuItem value="false">False</MenuItem>
      </RHFSelect>
      <RHFSelect label="Enable Inactivity Screen" name="enableInactivityScreen" variant="outlined">
        <MenuItem value="true">True</MenuItem>
        <MenuItem value="false">False</MenuItem>
      </RHFSelect>
      <RHFSelect label="Raindrop Type" name="raindropType" variant="outlined">
        <MenuItem value="RAINDROP_QUEUE">Queue</MenuItem>
        <MenuItem value="RAINDROP_MULTIVIEW">Multiview</MenuItem>
      </RHFSelect>
      {permissionToViewPublicKey && data?.providerPublicKey && (
        <>
          <Divider />
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ overflow: 'scroll' }}>
              <pre>{data?.providerPublicKey}</pre>
            </Box>
            <IconButton onClick={handleCopy}>
              <ContentCopyIcon />
            </IconButton>
          </Stack>
          {/* <Stack direction="row" spacing={2} alignItems="center">
            <Link href={demoUrl} target="_blank" rel="noreferrer">
              Open Demo
            </Link>
            <Link href={clientUrl} target="_blank" rel="noreferrer">
              Open Client
            </Link>
            <Link href={documentation} target="_blank" rel="noreferrer">
              Documentation
            </Link>
          </Stack> */}
        </>
      )}
    </Stack>
  );
};
